import { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Download, LogOut, Search, RefreshCcw, ShieldCheck, Mail, Phone,
  Calendar, Filter, Inbox, MessagesSquare, Sparkles,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { useAdminAuth } from "./AdminAuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(dt) {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return String(dt);
  }
}

export default function AdminDashboard() {
  const { token, logout } = useAdminAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/leads`, { headers }),
        axios.get(`${API}/admin/leads/stats`, { headers }),
      ]);
      setLeads(leadsRes.data || []);
      setStats(statsRes.data || null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to load inquiries.");
      if (err?.response?.status === 401) logout();
    } finally {
      setLoading(false);
    }
  }, [headers, logout]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    return leads.filter((l) => {
      if (sourceFilter !== "all" && l.source !== sourceFilter) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        l.name?.toLowerCase().includes(q) ||
        l.email?.toLowerCase().includes(q) ||
        l.service_interest?.toLowerCase().includes(q) ||
        l.message?.toLowerCase().includes(q) ||
        l.phone?.toLowerCase().includes(q)
      );
    });
  }, [leads, search, sourceFilter]);

  const downloadCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/leads.csv`, {
        headers,
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `genx-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded.");
    } catch {
      toast.error("Failed to download CSV.");
    }
  };

  const STATS = [
    { label: "Total Inquiries", value: stats?.total ?? "—", icon: Inbox },
    { label: "Contact Form", value: stats?.contact_form ?? "—", icon: MessagesSquare },
    { label: "Demo Requests", value: stats?.demo_request ?? "—", icon: Sparkles },
    { label: "Last 7 Days", value: stats?.last_7_days ?? "—", icon: Calendar },
  ];

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-slate-950 bg-grid text-slate-100">
      {/* Admin top bar */}
      <header className="border-b border-white/5 bg-slate-950/85 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 border border-cyan-400/40 bg-cyan-400/10 flex items-center justify-center">
              <ShieldCheck className="h-4 w-4 text-cyan-300" />
            </div>
            <div className="leading-none">
              <div className="text-[10px] tracking-[0.3em] uppercase text-cyan-400 font-mono">
                Admin
              </div>
              <div className="text-sm font-medium text-slate-50 mt-0.5">
                GenX Technologies · Dashboard
              </div>
            </div>
          </div>
          <Button
            data-testid="admin-logout-btn"
            onClick={() => {
              logout();
              toast.success("Logged out.");
            }}
            variant="outline"
            className="bg-transparent border border-white/15 text-slate-100 hover:bg-white/5 hover:text-cyan-300 hover:border-cyan-400/40 rounded-none h-9 px-4"
          >
            <LogOut className="h-4 w-4 mr-2" /> Logout
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {/* Heading */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-cyan-400 font-mono mb-2">
              / Inquiries
            </div>
            <h1 className="text-3xl md:text-4xl tracking-tight font-medium text-slate-50">
              All leads & demo requests
            </h1>
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="admin-refresh-btn"
              onClick={load}
              variant="outline"
              className="bg-transparent border border-white/15 text-slate-100 hover:bg-white/5 hover:text-cyan-300 hover:border-cyan-400/40 rounded-none h-10"
            >
              <RefreshCcw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              data-testid="admin-csv-btn"
              onClick={downloadCsv}
              className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 rounded-none font-semibold h-10 btn-glow"
            >
              <Download className="h-4 w-4 mr-2" /> Export CSV
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5 mb-10">
          {STATS.map((s) => (
            <div key={s.label} className="bg-slate-950 p-5 md:p-6 group hover:bg-slate-900 transition-colors">
              <div className="flex items-start justify-between mb-3">
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono">
                  {s.label}
                </div>
                <s.icon className="h-4 w-4 text-cyan-400/60 group-hover:text-cyan-400 transition-colors" />
              </div>
              <div className="text-3xl font-medium text-slate-50">{s.value}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="grid md:grid-cols-12 gap-3 mb-6">
          <div className="md:col-span-8 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              data-testid="admin-search"
              placeholder="Search by name, email, phone, service or message..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-slate-900 border border-white/10 text-slate-100 placeholder:text-slate-600 rounded-none h-11 pl-10 focus-visible:ring-1 focus-visible:ring-cyan-400 focus-visible:border-cyan-400 focus-visible:ring-offset-0"
            />
          </div>
          <div className="md:col-span-4">
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger
                data-testid="admin-source-filter"
                className="bg-slate-900 border border-white/10 text-slate-100 rounded-none h-11 focus:ring-1 focus:ring-cyan-400 focus:border-cyan-400 focus:ring-offset-0"
              >
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-slate-500" />
                  <SelectValue />
                </div>
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-white/10 text-slate-100 rounded-none">
                <SelectItem value="all" className="focus:bg-cyan-400/10 focus:text-cyan-200 rounded-none">
                  All sources
                </SelectItem>
                <SelectItem value="contact_form" className="focus:bg-cyan-400/10 focus:text-cyan-200 rounded-none">
                  Contact form
                </SelectItem>
                <SelectItem value="demo_request" className="focus:bg-cyan-400/10 focus:text-cyan-200 rounded-none">
                  Demo requests
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Table */}
        <div className="border border-white/5 overflow-hidden">
          {loading && !leads.length ? (
            <div className="p-20 flex items-center justify-center text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin mr-3" /> Loading inquiries...
            </div>
          ) : filtered.length === 0 ? (
            <div data-testid="admin-empty" className="p-20 text-center text-slate-400">
              <Inbox className="h-8 w-8 mx-auto mb-4 text-slate-700" />
              <div className="text-sm">No inquiries found.</div>
              <div className="text-xs text-slate-600 mt-1">
                {leads.length === 0
                  ? "When visitors submit the contact form, they'll appear here."
                  : "Try adjusting your search or filter."}
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-leads-table">
                <thead>
                  <tr className="bg-slate-900 border-b border-white/5">
                    {["Name", "Service", "Source", "Received", "Actions"].map((h) => (
                      <th
                        key={h}
                        className="text-left px-5 py-3 text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono font-normal"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((l) => (
                    <tr
                      key={l.id}
                      data-testid={`lead-row-${l.id}`}
                      className="border-b border-white/5 hover:bg-slate-900 transition-colors cursor-pointer"
                      onClick={() => setSelected(l)}
                    >
                      <td className="px-5 py-4">
                        <div className="text-slate-100 font-medium">{l.name}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{l.email}</div>
                      </td>
                      <td className="px-5 py-4 text-slate-300">{l.service_interest}</td>
                      <td className="px-5 py-4">
                        <span
                          className={`inline-block px-2 py-1 text-[10px] uppercase tracking-[0.15em] font-mono border ${
                            l.source === "demo_request"
                              ? "border-cyan-400/40 text-cyan-300 bg-cyan-400/5"
                              : "border-white/15 text-slate-400"
                          }`}
                        >
                          {l.source === "demo_request" ? "Demo" : "Contact"}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-slate-400 text-xs whitespace-nowrap">
                        {fmt(l.created_at)}
                      </td>
                      <td className="px-5 py-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelected(l);
                          }}
                          className="bg-transparent border border-white/15 text-slate-300 hover:bg-white/5 hover:text-cyan-300 rounded-none h-8 text-xs"
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Result count */}
        {!loading && (
          <div className="mt-4 text-xs text-slate-500 font-mono">
            Showing {filtered.length} of {leads.length} inquiries
          </div>
        )}
      </div>

      {/* Detail panel */}
      {selected && (
        <div
          data-testid="lead-detail-overlay"
          className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
          onClick={() => setSelected(null)}
        >
          <div
            data-testid="lead-detail-panel"
            className="bg-slate-950 border border-cyan-400/30 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-white/5 px-6 py-4 flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-[0.3em] text-cyan-400 font-mono">
                Inquiry Detail
              </div>
              <button
                onClick={() => setSelected(null)}
                className="text-slate-400 hover:text-cyan-300 text-sm"
                data-testid="lead-detail-close"
              >
                Close ×
              </button>
            </div>
            <div className="p-6 md:p-8 space-y-6">
              <div>
                <h2 className="text-2xl md:text-3xl font-medium text-slate-50 tracking-tight mb-1">
                  {selected.name}
                </h2>
                <div className="text-xs text-slate-500 font-mono">
                  {fmt(selected.created_at)} · {selected.source}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-px bg-white/5 border border-white/5">
                <a
                  href={`mailto:${selected.email}`}
                  className="bg-slate-950 p-4 hover:bg-slate-900 transition-colors group block"
                >
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono mb-1">
                    <Mail className="h-3 w-3" /> Email
                  </div>
                  <div className="text-sm text-slate-100 group-hover:text-cyan-300 break-all">
                    {selected.email}
                  </div>
                </a>
                <a
                  href={selected.phone ? `tel:${selected.phone.replace(/\s+/g, "")}` : "#"}
                  className="bg-slate-950 p-4 hover:bg-slate-900 transition-colors group block"
                >
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono mb-1">
                    <Phone className="h-3 w-3" /> Phone
                  </div>
                  <div className="text-sm text-slate-100 group-hover:text-cyan-300">
                    {selected.phone || "—"}
                  </div>
                </a>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono mb-2">
                  Service Interest
                </div>
                <div className="inline-block px-3 py-1.5 border border-cyan-400/30 bg-cyan-400/5 text-cyan-200 text-sm">
                  {selected.service_interest}
                </div>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono mb-2">
                  Message
                </div>
                <div className="bg-slate-900 border border-white/10 p-5 text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">
                  {selected.message}
                </div>
              </div>

              <div className="flex flex-wrap gap-2 pt-2">
                <a href={`mailto:${selected.email}?subject=Re: Your GenX inquiry`}>
                  <Button className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 rounded-none font-semibold h-10 btn-glow">
                    <Mail className="h-4 w-4 mr-2" /> Reply by Email
                  </Button>
                </a>
                {selected.phone && (
                  <a href={`tel:${selected.phone.replace(/\s+/g, "")}`}>
                    <Button
                      variant="outline"
                      className="bg-transparent border border-white/15 text-slate-100 hover:bg-white/5 hover:text-cyan-300 hover:border-cyan-400/40 rounded-none h-10"
                    >
                      <Phone className="h-4 w-4 mr-2" /> Call
                    </Button>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
